import React, { useState, useRef } from 'react';
import Image from '../../../components/AppImage';
import UploadComponent from '../../../components/UploadComponent';
import { postService } from '../../../lib/postService';

const PostCreator = ({ user }) => {
  const [postContent, setPostContent] = useState('');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [isPosting, setIsPosting] = useState(false);
  const uploadTriggerRef = useRef(null);

  const handleMediaSuccess = (url, type) => {
    setMediaUrl(url);
    setMediaType(type || 'image');
  };

  const handlePost = async () => {
    if (!user || !user.id) {
      alert("Please log in to create a post.");
      return;
    }

    if (!postContent?.trim() && !mediaUrl) return;

    try {
      setIsPosting(true);
      await postService.createPost(postContent, user.id, mediaUrl, mediaType);
      setPostContent('');
      setMediaUrl(null);
      setMediaType('image');
      // Clear the upload preview
      uploadTriggerRef.current?.clear();
    } catch (error) {
      console.error("Error creating post:", error);
      alert("Failed to create post. Please try again.");
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <section className="glass-panel rose-gold-frame rounded-xl p-8 space-y-6 mb-8">
      <div className="flex items-start space-x-6">
        <div className="w-12 h-12 rounded-full bg-surface-container-highest flex-shrink-0 p-[1px]">
          <Image
            src={user?.avatar}
            alt={user?.avatarAlt}
            className="w-full h-full object-cover rounded-full"
          />
        </div>
        <div className="flex-1 space-y-2">
          <textarea
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            className="w-full bg-transparent border-none focus:ring-0 text-lg text-white placeholder-white/40 resize-none outline-none"
            placeholder="Share a fragment of your reality..."
            rows={postContent.split('\n').length > 2 ? postContent.split('\n').length : 2}
          />

          {/* UploadComponent — renders preview inline, file dialog via triggerRef */}
          <UploadComponent
            triggerRef={uploadTriggerRef}
            onUploadSuccess={handleMediaSuccess}
            compact
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="flex items-center space-x-6">
          {/* Media button opens the UploadComponent file picker */}
          <button
            onClick={() => uploadTriggerRef.current?.open()}
            className="group flex items-center space-x-2 text-white/60 hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">image</span>
            <span className="text-xs uppercase tracking-widest font-semibold">Media</span>
          </button>
          <button 
            onClick={() => uploadTriggerRef.current?.open()}
            className="group flex items-center space-x-2 text-white/60 hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">videocam</span>
            <span className="text-xs uppercase tracking-widest font-semibold">Capture</span>
          </button>
          <button 
            onClick={() => uploadTriggerRef.current?.open()}
            className="group flex items-center space-x-2 text-white/60 hover:text-[#00ffcc] transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">view_in_ar</span>
            <span className="text-xs uppercase tracking-widest font-semibold">3D Model</span>
          </button>
        </div>

        <div className="flex items-center space-x-6">
          <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-white/5 rounded-full border border-white/10">
            <span
              className="material-symbols-outlined text-[16px] text-white"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              cloud_done
            </span>
            <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">
              Auto-save to Profile
            </span>
          </div>
          <button
            onClick={handlePost}
            disabled={isPosting || (!postContent?.trim() && !mediaUrl)}
            className="px-8 py-2 bg-white text-black text-xs uppercase tracking-widest font-semibold rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isPosting ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PostCreator;